import { useMemo, useState } from "react";
import { SUPPORT_WHATSAPP_URL } from "../../utils/whatsappCall";
import "./LandingFaqSection.css";

const TABS = [
  { id: "all", label: "All" },
  { id: "booking", label: "Booking & Hiring" },
  { id: "trust", label: "Trust & Verification" },
  { id: "payments", label: "Payments & Shifts" },
  { id: "support", label: "Support & Safety" },
];

const FAQ_ITEMS = [
  {
    id: "verify-professionals",
    category: "trust",
    question: "How do you verify care professionals?",
    answer:
      "NCID provides identity verification for care professionals on NEMA CareHub. It should not be interpreted as confirmation of professional qualifications, medical registration, criminal background clearance or clinical competence.",
  },
  {
    id: "pay-directly",
    category: "payments",
    question: "Do I pay the care professional directly?",
    answer:
      "Yes. The payment model is based on direct payment to the care professional. Applicable platform or transaction fees and other payment terms should be reviewed before proceeding.",
  },
  {
    id: "no-show",
    category: "support",
    question: "What happens if a care professional is unavailable?",
    answer:
      "Contact NEMA CareHub to discuss your options. Any replacement assistance is subject to the applicable replacement policy and service terms.",
  },
  {
    id: "care-manager-available",
    category: "booking",
    question: "Is a Care Manager available?",
    answer:
      "An optional Care Manager service may be available for staffing and scheduling coordination. Please review the applicable service scope and terms.",
  },
  {
    id: "ncid-guarantee",
    category: "trust",
    question: "Does NCID guarantee the quality or safety of care?",
    answer:
      "No. NCID provides identity verification only. Families should discuss care requirements directly with the professional and independently assess suitability before hiring.",
  },
  {
    id: "additional-fees",
    category: "payments",
    question: "Are there additional fees?",
    answer:
      "Platform or transaction fees may apply. Review the applicable pricing and terms before confirming your arrangement.",
  },
];

function FaqChevron({ open }) {
  return (
    <svg
      className={`landing-faq__chevron${open ? " is-open" : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SupportHeadsetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M3 11v2a2 2 0 002 2h1v-4H5a2 2 0 00-2 2z" />
      <path d="M21 11v2a2 2 0 01-2 2h-1v-4h1a2 2 0 012 2z" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
      <path d="M12 17v3" strokeLinecap="round" />
      <path d="M8 20h8" strokeLinecap="round" />
    </svg>
  );
}

export default function LandingFaqSection() {
  const [activeTab, setActiveTab] = useState("all");
  const [openId, setOpenId] = useState(null);

  const visibleItems = useMemo(() => {
    if (activeTab === "all") return FAQ_ITEMS;
    return FAQ_ITEMS.filter((item) => item.category === activeTab);
  }, [activeTab]);

  function handleTabChange(tabId) {
    setActiveTab(tabId);
    setOpenId(null);
  }

  function toggleItem(id) {
    setOpenId((prev) => (prev === id ? null : id));
  }

  return (
    <section className="landing-faq" aria-labelledby="landing-faq-heading">
      <header className="landing-faq__header">
        <p className="landing-faq__eyebrow">FREQUENTLY ASKED QUESTIONS</p>
        <h2 id="landing-faq-heading" className="landing-faq__title">
          Got questions? We&apos;ve got <span className="landing-faq__title-mark">answers</span>
        </h2>
        <p className="landing-faq__subtitle">
          Direct hiring, identity verification and payment terms — explained.
        </p>
      </header>

      <div className="landing-faq__tabs" role="tablist" aria-label="FAQ categories">
        {TABS.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`landing-faq__tab${selected ? " is-active" : ""}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="landing-faq__list" role="list">
        {visibleItems.map((item, index) => {
          const isOpen = openId === item.id;
          const num = String(index + 1).padStart(2, "0");
          const panelId = `landing-faq-panel-${item.id}`;
          const buttonId = `landing-faq-trigger-${item.id}`;

          return (
            <article key={item.id} className="landing-faq__item" role="listitem">
              <h3 className="landing-faq__question-wrap">
                <button
                  type="button"
                  id={buttonId}
                  className={`landing-faq__trigger${isOpen ? " is-open" : ""}`}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => toggleItem(item.id)}
                >
                  <span className="landing-faq__num" aria-hidden>
                    {num}
                  </span>
                  <span className="landing-faq__question">{item.question}</span>
                  <FaqChevron open={isOpen} />
                </button>
              </h3>
              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                className={`landing-faq__panel${isOpen ? " is-open" : ""}`}
                hidden={!isOpen}
              >
                <p>{item.answer}</p>
              </div>
            </article>
          );
        })}
      </div>

      <aside className="landing-faq__support">
        <span className="landing-faq__support-icon" aria-hidden>
          <SupportHeadsetIcon />
        </span>
        <div className="landing-faq__support-copy">
          <strong>Still have questions?</strong>
          <span>Our care experts are here to help.</span>
        </div>
        <a
          className="landing-faq__support-btn"
          href={SUPPORT_WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Chat with Us
        </a>
      </aside>
    </section>
  );
}
